import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { Box, RefreshCw, CheckCircle } from 'lucide-react';
import { ErrorActionProps } from '@/lib/errorActions';

interface Mold {
  id: string;
  name: string;
  code: string;
  status: string;
}

export function QuickMoldAssign({ workOrder, onSuccess, onCancel }: ErrorActionProps) {
  const { toast } = useToast();
  const [molds, setMolds] = useState<Mold[]>([]);
  const [selectedMoldId, setSelectedMoldId] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchMolds = async () => {
      const { data, error } = await api
        .from('molds')
        .select('id, name, code, status')
        .eq('status', 'available')
        .order('name');

      if (!error && data) {
        setMolds(data);
      }
      setLoading(false);
    };
    fetchMolds();
  }, []);

  const handleAssign = async () => {
    if (!selectedMoldId) return;

    setSubmitting(true);
    try {
      const { error } = await api
        .from('work_orders')
        .update({ mold_id: selectedMoldId })
        .eq('id', workOrder.id);

      if (error) throw error;

      await api
        .from('molds')
        .update({ status: 'in_use' })
        .eq('id', selectedMoldId);

      toast({
        title: 'Molde asignado',
        description: `Molde asignado a ${workOrder.order_number}`,
      });

      onSuccess();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo asignar el molde',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Box className="w-5 h-5 text-primary" />
            Asignar Molde
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">Orden de trabajo</p>
            <p className="font-semibold">{workOrder.order_number}</p>
            {workOrder.product_name && (
              <p className="text-sm text-muted-foreground">{workOrder.product_name}</p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Selecciona un molde disponible</label>
            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Cargando moldes...
              </div>
            ) : molds.length === 0 ? (
              <p className="text-amber-500 text-sm">No hay moldes disponibles</p>
            ) : (
              <Select value={selectedMoldId} onValueChange={setSelectedMoldId}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar molde" />
                </SelectTrigger>
                <SelectContent>
                  {molds.map((mold) => (
                    <SelectItem key={mold.id} value={mold.id}>
                      <div className="flex items-center gap-2">
                        <span>{mold.name}</span>
                        <Badge variant="outline" className="text-xs font-mono">{mold.code}</Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button onClick={handleAssign} disabled={!selectedMoldId || submitting}>
            {submitting ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-2" />
            )}
            Asignar y Continuar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
